import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import api from '../api';
import {
  UserCheck, Clock, AlertTriangle, CheckCircle, XCircle, Shield,
  Search, Plus, Eye, Star, MessageSquare, ChevronRight, ChevronDown,
  Calendar, Users, TrendingUp, Loader2, Settings, Trash2, ArrowRight, Bell
} from 'lucide-react';

const ProbationTracker = ({ user, setUser }) => {
  const [trackers, setTrackers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [employees, setEmployees] = useState([]);

  // Dialogs
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showRecommend, setShowRecommend] = useState(null);
  const [showApprove, setShowApprove] = useState(null);
  const [showFeedback, setShowFeedback] = useState(null);

  // Settings
  const [settings, setSettings] = useState([]);
  const [settingForm, setSettingForm] = useState(null);

  // Create form
  const [createEmpId, setCreateEmpId] = useState('');
  const [createDuration, setCreateDuration] = useState('');
  const [empSearch, setEmpSearch] = useState('');
  const [creating, setCreating] = useState(false);

  // Recommend/Approve forms
  const [actionForm, setActionForm] = useState({ action: 'confirm', reason: '', extension_days: 30 });

  // Feedback form
  const [feedbackForm, setFeedbackForm] = useState({ rating: 3, comments: '', strengths: '', areas_of_improvement: '' });

  const fetchAll = useCallback(async () => {
    try {
      const [tRes, sRes, dRes, dgRes, eRes] = await Promise.all([
        api.get('/probation/trackers'),
        api.get('/probation/stats'),
        api.get('/departments'),
        api.get('/designations'),
        api.get('/employees'),
      ]);
      setTrackers(tRes.data);
      setStats(sRes.data);
      setDepartments(Array.isArray(dRes.data) ? dRes.data : []);
      setDesignations(Array.isArray(dgRes.data) ? dgRes.data : []);
      const emps = Array.isArray(eRes.data) ? eRes.data : eRes.data?.employees || [];
      setEmployees(emps);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await api.get('/probation/settings');
      setSettings(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Handlers
  const handleCreate = async () => {
    if (!createEmpId) { toast.error('Select an employee'); return; }
    setCreating(true);
    try {
      await api.post('/probation/trackers', {
        employee_id: createEmpId,
        duration_days: createDuration ? parseInt(createDuration) : undefined,
      });
      toast.success('Probation tracker created');
      setShowCreate(false);
      setCreateEmpId('');
      setCreateDuration('');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create');
    } finally {
      setCreating(false);
    }
  };

  const handleChecklist = async (trackerId, mIdx, cIdx, completed) => {
    try {
      await api.put(`/probation/trackers/${trackerId}/checklist`, {
        milestone_index: mIdx, checklist_index: cIdx, completed
      });
      // Refresh detail
      const res = await api.get(`/probation/trackers/${trackerId}`);
      setShowDetail(res.data);
      fetchAll();
    } catch (err) {
      toast.error('Failed to update checklist');
    }
  };

  const handleFeedback = async () => {
    if (!showFeedback) return;
    try {
      await api.post(`/probation/trackers/${showFeedback.trackerId}/feedback`, {
        milestone_index: showFeedback.milestoneIndex,
        ...feedbackForm
      });
      toast.success('Feedback submitted');
      setShowFeedback(null);
      const res = await api.get(`/probation/trackers/${showFeedback.trackerId}`);
      setShowDetail(res.data);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit feedback');
    }
  };

  const handleRecommend = async () => {
    if (!showRecommend) return;
    try {
      await api.put(`/probation/trackers/${showRecommend.id}/recommend`, actionForm);
      toast.success('Recommendation submitted');
      setShowRecommend(null);
      fetchAll();
      setShowDetail(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    }
  };

  const handleApprove = async () => {
    if (!showApprove) return;
    try {
      await api.put(`/probation/trackers/${showApprove.id}/approve`, actionForm);
      toast.success(`Probation ${actionForm.action}ed`);
      setShowApprove(null);
      fetchAll();
      setShowDetail(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleSaveSetting = async () => {
    if (!settingForm) return;
    try {
      const payload = {
        department_id: settingForm.department_id || null,
        designation_id: settingForm.designation_id || null,
        duration_days: parseInt(settingForm.duration_days) || 90,
        milestone_days: (settingForm.milestone_days_str || '30,60,90').split(',').map(d => parseInt(d.trim())).filter(Boolean),
        checklist_template: settingForm.checklist_template || undefined,
      };
      if (settingForm.id) {
        await api.put(`/probation/settings/${settingForm.id}`, payload);
      } else {
        await api.post('/probation/settings', payload);
      }
      toast.success('Setting saved');
      setSettingForm(null);
      fetchSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const handleDeleteSetting = async (id) => {
    if (!window.confirm('Delete this setting?')) return;
    try {
      await api.delete(`/probation/settings/${id}`);
      toast.success('Deleted');
      fetchSettings();
    } catch { toast.error('Failed'); }
  };

  const getStatusBadge = (status, isOverdue) => {
    if (isOverdue) return <Badge className="bg-red-100 text-red-700">Overdue</Badge>;
    const map = {
      active: <Badge className="bg-blue-100 text-blue-700">Active</Badge>,
      pending_recommendation: <Badge className="bg-amber-100 text-amber-700">Awaiting Manager</Badge>,
      pending_approval: <Badge className="bg-purple-100 text-purple-700">Pending HR Approval</Badge>,
      confirmed: <Badge className="bg-emerald-100 text-emerald-700">Confirmed</Badge>,
      terminated: <Badge className="bg-red-100 text-red-700">Terminated</Badge>,
    };
    return map[status] || <Badge variant="outline">{status}</Badge>;
  };

  const filteredTrackers = trackers.filter(t => {
    if (statusFilter !== 'all' && t.status !== statusFilter) return false;
    if (search) {
      const s = search.toLowerCase();
      return t.employee_name?.toLowerCase().includes(s) || t.employee_code?.toLowerCase().includes(s);
    }
    return true;
  });

  const deptMap = Object.fromEntries(departments.map(d => [d.id, d.name]));
  const desigMap = Object.fromEntries(designations.map(d => [d.id, d.title || d.name]));

  // Employees without active tracker
  const trackedEmpIds = new Set(trackers.filter(t => ['active', 'pending_recommendation', 'pending_approval'].includes(t.status)).map(t => t.employee_id));
  const availableEmps = employees.filter(e => {
    if (trackedEmpIds.has(e.id)) return false;
    if (empSearch) {
      const s = empSearch.toLowerCase();
      const name = (e.name || `${e.first_name || ''} ${e.last_name || ''}`.trim()).toLowerCase();
      return name.includes(s) || (e.employee_id || '').toLowerCase().includes(s);
    }
    return true;
  });

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6" data-testid="probation-tracker-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" data-testid="page-title">Probation Tracker</h1>
            <p className="text-slate-500 text-sm">Track new hires through probation milestones and approvals</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={async () => {
              try {
                await api.post('/probation/trigger-reminders');
                toast.success('Probation reminders triggered');
              } catch { toast.error('Failed to trigger reminders'); }
            }} data-testid="trigger-reminders-btn">
              <Bell size={16} className="mr-1" /> Send Reminders
            </Button>
            <Button variant="outline" onClick={() => { setShowSettings(true); fetchSettings(); }} data-testid="settings-btn">
              <Settings size={16} className="mr-1" /> Settings
            </Button>
            <Button onClick={() => { setShowCreate(true); setCreateEmpId(''); setEmpSearch(''); }} data-testid="create-tracker-btn">
              <Plus size={16} className="mr-1" /> Start Probation
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {[
            { label: 'Total', value: stats.total || 0, color: 'slate', icon: Users },
            { label: 'Active', value: stats.active || 0, color: 'blue', icon: Clock },
            { label: 'Expiring Soon', value: stats.expiring_soon || 0, color: 'amber', icon: AlertTriangle },
            { label: 'Pending Approval', value: stats.pending_approval || 0, color: 'purple', icon: Shield },
            { label: 'Confirmed', value: stats.confirmed || 0, color: 'emerald', icon: UserCheck },
            { label: 'Overdue', value: stats.overdue || 0, color: 'red', icon: XCircle },
          ].map(s => (
            <Card key={s.label} className={`border-l-4 border-l-${s.color}-400`}>
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-xs text-${s.color}-600`}>{s.label}</p>
                    <p className={`text-xl font-bold text-${s.color}-700`}>{s.value}</p>
                  </div>
                  <s.icon size={18} className={`text-${s.color}-400`} />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Search by name or code..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="search-input" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44" data-testid="status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="pending_approval">Pending Approval</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="terminated">Terminated</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Trackers Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="py-16 text-center text-slate-400"><Loader2 className="animate-spin mx-auto mb-2" /> Loading...</div>
            ) : filteredTrackers.length === 0 ? (
              <div className="py-16 text-center text-slate-400">
                <UserCheck size={40} className="mx-auto mb-3 text-slate-300" />
                <p>No probation trackers found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-slate-50 text-left text-slate-500">
                      <th className="p-3 font-medium">Employee</th>
                      <th className="p-3 font-medium">Department</th>
                      <th className="p-3 font-medium text-center">Duration</th>
                      <th className="p-3 font-medium text-center">Progress</th>
                      <th className="p-3 font-medium text-center">Days Left</th>
                      <th className="p-3 font-medium text-center">Status</th>
                      <th className="p-3 font-medium">Next Milestone</th>
                      <th className="p-3 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTrackers.map(t => (
                      <tr key={t.id} className="border-b last:border-0 hover:bg-slate-50 transition-colors" data-testid={`tracker-row-${t.id}`}>
                        <td className="p-3">
                          <p className="font-medium text-slate-800">{t.employee_name}</p>
                          <p className="text-xs text-slate-400">{t.employee_code}</p>
                        </td>
                        <td className="p-3 text-slate-600">{t.department_name || '-'}</td>
                        <td className="p-3 text-center text-slate-600">{t.duration_days}d</td>
                        <td className="p-3">
                          <div className="flex items-center justify-center gap-2">
                            <div className="w-16 h-2 bg-slate-200 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full ${t.progress >= 100 ? 'bg-emerald-500' : t.progress > 50 ? 'bg-indigo-500' : 'bg-amber-500'}`} style={{ width: `${t.progress}%` }} />
                            </div>
                            <span className="text-xs text-slate-500">{t.progress}%</span>
                          </div>
                        </td>
                        <td className="p-3 text-center">
                          {t.status === 'confirmed' || t.status === 'terminated' ? (
                            <span className="text-slate-400">-</span>
                          ) : (
                            <span className={`text-sm font-medium ${t.days_remaining <= 7 ? 'text-red-600' : t.days_remaining <= 14 ? 'text-amber-600' : 'text-slate-700'}`}>
                              {t.is_overdue ? 'Overdue' : `${t.days_remaining}d`}
                            </span>
                          )}
                        </td>
                        <td className="p-3 text-center">{getStatusBadge(t.status, t.is_overdue)}</td>
                        <td className="p-3 text-slate-500 text-xs">{t.next_milestone || '-'}</td>
                        <td className="p-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="icon" className="h-7 w-7" title="View Details" onClick={async () => {
                              const res = await api.get(`/probation/trackers/${t.id}`);
                              setShowDetail(res.data);
                            }} data-testid={`view-btn-${t.id}`}>
                              <Eye size={14} />
                            </Button>
                            {t.status === 'active' && (
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-amber-600" title="Recommend" onClick={() => { setShowRecommend(t); setActionForm({ action: 'confirm', reason: '', extension_days: 30 }); }}>
                                <ArrowRight size={14} />
                              </Button>
                            )}
                            {t.status === 'pending_approval' && (
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-emerald-600" title="Approve" onClick={() => { setShowApprove(t); setActionForm({ action: t.manager_recommendation?.action || 'confirm', reason: '', extension_days: 30 }); }}>
                                <Shield size={14} />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ==================== CREATE DIALOG ==================== */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Plus size={18} className="text-indigo-500" /> Start Probation Tracker</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Search Employee</Label>
              <Input placeholder="Search..." value={empSearch} onChange={e => setEmpSearch(e.target.value)} data-testid="emp-search" />
            </div>
            <div className="max-h-48 overflow-y-auto border rounded-lg divide-y">
              {availableEmps.slice(0, 15).map(emp => {
                const name = emp.name || `${emp.first_name || ''} ${emp.last_name || ''}`.trim();
                return (
                  <button key={emp.id} className={`w-full text-left px-3 py-2 hover:bg-indigo-50 flex items-center justify-between ${createEmpId === emp.id ? 'bg-indigo-50 ring-1 ring-indigo-300' : ''}`} onClick={() => setCreateEmpId(emp.id)} data-testid={`emp-opt-${emp.id}`}>
                    <div>
                      <p className="text-sm font-medium">{name}</p>
                      <p className="text-xs text-slate-400">{emp.employee_id} | {deptMap[emp.department_id] || ''}</p>
                    </div>
                    {createEmpId === emp.id && <CheckCircle size={16} className="text-indigo-600" />}
                  </button>
                );
              })}
              {availableEmps.length === 0 && <p className="p-4 text-sm text-center text-slate-400">No available employees</p>}
            </div>
            <div>
              <Label>Custom Duration (days) — leave blank for default</Label>
              <Input type="number" value={createDuration} onChange={e => setCreateDuration(e.target.value)} placeholder="e.g. 90" data-testid="duration-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={!createEmpId || creating} data-testid="confirm-create-btn">
              {creating && <Loader2 size={14} className="animate-spin mr-1" />} Create Tracker
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ==================== DETAIL DIALOG ==================== */}
      {showDetail && (
        <Dialog open={!!showDetail} onOpenChange={() => setShowDetail(null)}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserCheck size={18} className="text-indigo-500" /> {showDetail.employee_name} — Probation
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-5">
              {/* Info Bar */}
              <div className="flex flex-wrap items-center gap-2 bg-slate-50 p-3 rounded-lg text-sm">
                <Badge variant="outline">{showDetail.employee_code}</Badge>
                <Badge variant="outline">{showDetail.department_name}</Badge>
                <Badge variant="outline">{showDetail.designation_name}</Badge>
                {getStatusBadge(showDetail.status, showDetail.is_overdue)}
                <span className="ml-auto text-slate-500">{showDetail.start_date} → {showDetail.end_date} ({showDetail.duration_days}d)</span>
              </div>

              {/* Progress */}
              <div>
                <div className="flex justify-between text-xs text-slate-500 mb-1">
                  <span>Overall Progress</span>
                  <span>{showDetail.progress}%</span>
                </div>
                <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${showDetail.progress >= 100 ? 'bg-emerald-500' : 'bg-indigo-500'}`} style={{ width: `${showDetail.progress}%` }} />
                </div>
              </div>

              {/* Manager Recommendation */}
              {showDetail.manager_recommendation && (
                <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg">
                  <p className="text-xs font-semibold text-amber-700 mb-1">Manager Recommendation</p>
                  <p className="text-sm"><strong className="capitalize">{showDetail.manager_recommendation.action}</strong> — {showDetail.manager_recommendation.reason}</p>
                  <p className="text-xs text-amber-500 mt-1">By {showDetail.manager_recommendation.recommended_by} on {new Date(showDetail.manager_recommendation.recommended_at).toLocaleDateString()}</p>
                </div>
              )}

              {/* HR Decision */}
              {showDetail.hr_decision && (
                <div className={`border p-3 rounded-lg ${showDetail.hr_decision.action === 'confirm' ? 'bg-emerald-50 border-emerald-200' : showDetail.hr_decision.action === 'terminate' ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'}`}>
                  <p className="text-xs font-semibold mb-1" style={{ color: showDetail.hr_decision.action === 'confirm' ? '#047857' : showDetail.hr_decision.action === 'terminate' ? '#B91C1C' : '#1D4ED8' }}>HR Decision</p>
                  <p className="text-sm"><strong className="capitalize">{showDetail.hr_decision.action}</strong> — {showDetail.hr_decision.reason}</p>
                  <p className="text-xs mt-1 opacity-70">By {showDetail.hr_decision.decided_by} on {new Date(showDetail.hr_decision.decided_at).toLocaleDateString()}</p>
                </div>
              )}

              {/* Milestones */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-slate-700">Milestones</h3>
                {(showDetail.milestones || []).map((m, mIdx) => (
                  <Card key={mIdx} className={`${m.completed ? 'border-emerald-200 bg-emerald-50/30' : ''}`} data-testid={`milestone-${mIdx}`}>
                    <CardHeader className="pb-2 pt-3 px-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {m.completed ? <CheckCircle size={16} className="text-emerald-500" /> : <Clock size={16} className="text-slate-400" />}
                          <CardTitle className="text-sm">{m.label}</CardTitle>
                          <span className="text-xs text-slate-400">Due: {m.due_date}</span>
                        </div>
                        {!m.feedback && showDetail.status === 'active' && (
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setShowFeedback({ trackerId: showDetail.id, milestoneIndex: mIdx }); setFeedbackForm({ rating: 3, comments: '', strengths: '', areas_of_improvement: '' }); }} data-testid={`feedback-btn-${mIdx}`}>
                            <MessageSquare size={12} className="mr-1" /> Add Feedback
                          </Button>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="px-4 pb-3">
                      {/* Checklist */}
                      <div className="space-y-1.5 mb-3">
                        {(m.checklist || []).map((c, cIdx) => (
                          <label key={cIdx} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 p-1 rounded" data-testid={`checklist-${mIdx}-${cIdx}`}>
                            <input
                              type="checkbox"
                              checked={c.completed}
                              onChange={e => handleChecklist(showDetail.id, mIdx, cIdx, e.target.checked)}
                              disabled={showDetail.status !== 'active'}
                              className="rounded"
                            />
                            <span className={c.completed ? 'line-through text-slate-400' : 'text-slate-700'}>{c.item}</span>
                            {c.required && <span className="text-red-400 text-xs">*</span>}
                            {c.completed_by && <span className="text-xs text-slate-400 ml-auto">by {c.completed_by}</span>}
                          </label>
                        ))}
                      </div>

                      {/* Feedback display */}
                      {m.feedback && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                          <div className="flex items-center gap-2 mb-1">
                            <div className="flex gap-0.5">
                              {[1, 2, 3, 4, 5].map(s => (
                                <Star key={s} size={14} className={s <= m.feedback.rating ? 'text-amber-400 fill-amber-400' : 'text-slate-300'} />
                              ))}
                            </div>
                            <span className="text-xs text-blue-600">by {m.feedback.submitted_by}</span>
                          </div>
                          <p className="text-slate-700">{m.feedback.comments}</p>
                          {m.feedback.strengths && <p className="text-xs text-emerald-600 mt-1"><strong>Strengths:</strong> {m.feedback.strengths}</p>}
                          {m.feedback.areas_of_improvement && <p className="text-xs text-amber-600 mt-0.5"><strong>Improve:</strong> {m.feedback.areas_of_improvement}</p>}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 pt-2 border-t">
                {showDetail.status === 'active' && (
                  <Button onClick={() => { setShowRecommend(showDetail); setActionForm({ action: 'confirm', reason: '', extension_days: 30 }); }} className="bg-amber-600 hover:bg-amber-700" data-testid="recommend-btn">
                    <ArrowRight size={14} className="mr-1" /> Manager Recommend
                  </Button>
                )}
                {showDetail.status === 'pending_approval' && (
                  <Button onClick={() => { setShowApprove(showDetail); setActionForm({ action: showDetail.manager_recommendation?.action || 'confirm', reason: '', extension_days: 30 }); }} className="bg-emerald-600 hover:bg-emerald-700" data-testid="approve-btn">
                    <Shield size={14} className="mr-1" /> HR Approve/Decide
                  </Button>
                )}
                <Button variant="outline" onClick={() => setShowDetail(null)}>Close</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* ==================== RECOMMEND DIALOG ==================== */}
      <Dialog open={!!showRecommend} onOpenChange={() => setShowRecommend(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Manager Recommendation — {showRecommend?.employee_name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Recommended Action</Label>
              <Select value={actionForm.action} onValueChange={v => setActionForm({ ...actionForm, action: v })}>
                <SelectTrigger data-testid="recommend-action-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="confirm">Confirm Employment</SelectItem>
                  <SelectItem value="extend">Extend Probation</SelectItem>
                  <SelectItem value="terminate">Terminate</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {actionForm.action === 'extend' && (
              <div>
                <Label>Extension Duration (days)</Label>
                <Input type="number" value={actionForm.extension_days} onChange={e => setActionForm({ ...actionForm, extension_days: parseInt(e.target.value) || 30 })} data-testid="extension-days-input" />
              </div>
            )}
            <div>
              <Label>Reason / Comments</Label>
              <Textarea value={actionForm.reason} onChange={e => setActionForm({ ...actionForm, reason: e.target.value })} placeholder="Provide your recommendation details..." data-testid="recommend-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRecommend(null)}>Cancel</Button>
            <Button onClick={handleRecommend} disabled={!actionForm.reason} data-testid="submit-recommend-btn">Submit Recommendation</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ==================== APPROVE DIALOG ==================== */}
      <Dialog open={!!showApprove} onOpenChange={() => setShowApprove(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>HR Decision — {showApprove?.employee_name}</DialogTitle></DialogHeader>
          {showApprove?.manager_recommendation && (
            <div className="bg-amber-50 p-3 rounded-lg text-sm border border-amber-200 mb-2">
              <p className="text-xs font-semibold text-amber-700">Manager Recommends: <span className="capitalize">{showApprove.manager_recommendation.action}</span></p>
              <p className="text-slate-600 mt-1">{showApprove.manager_recommendation.reason}</p>
            </div>
          )}
          <div className="space-y-4">
            <div>
              <Label>Your Decision</Label>
              <Select value={actionForm.action} onValueChange={v => setActionForm({ ...actionForm, action: v })}>
                <SelectTrigger data-testid="approve-action-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="confirm">Confirm Employment</SelectItem>
                  <SelectItem value="extend">Extend Probation</SelectItem>
                  <SelectItem value="terminate">Terminate</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {actionForm.action === 'extend' && (
              <div>
                <Label>Extension Duration (days)</Label>
                <Input type="number" value={actionForm.extension_days} onChange={e => setActionForm({ ...actionForm, extension_days: parseInt(e.target.value) || 30 })} data-testid="approve-extension-input" />
              </div>
            )}
            <div>
              <Label>Reason</Label>
              <Textarea value={actionForm.reason} onChange={e => setActionForm({ ...actionForm, reason: e.target.value })} placeholder="Provide your decision reason..." data-testid="approve-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApprove(null)}>Cancel</Button>
            <Button onClick={handleApprove} disabled={!actionForm.reason} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-approve-btn">Confirm Decision</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ==================== FEEDBACK DIALOG ==================== */}
      <Dialog open={!!showFeedback} onOpenChange={() => setShowFeedback(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><MessageSquare size={18} className="text-blue-500" /> Milestone Feedback</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Rating</Label>
              <div className="flex gap-1 mt-1">
                {[1, 2, 3, 4, 5].map(s => (
                  <button key={s} onClick={() => setFeedbackForm({ ...feedbackForm, rating: s })} className="p-1" data-testid={`star-${s}`}>
                    <Star size={24} className={s <= feedbackForm.rating ? 'text-amber-400 fill-amber-400' : 'text-slate-300'} />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>Comments <span className="text-red-400">*</span></Label>
              <Textarea value={feedbackForm.comments} onChange={e => setFeedbackForm({ ...feedbackForm, comments: e.target.value })} placeholder="Overall assessment..." data-testid="feedback-comments" />
            </div>
            <div>
              <Label>Key Strengths</Label>
              <Input value={feedbackForm.strengths} onChange={e => setFeedbackForm({ ...feedbackForm, strengths: e.target.value })} placeholder="e.g. Quick learner, team player" data-testid="feedback-strengths" />
            </div>
            <div>
              <Label>Areas of Improvement</Label>
              <Input value={feedbackForm.areas_of_improvement} onChange={e => setFeedbackForm({ ...feedbackForm, areas_of_improvement: e.target.value })} placeholder="e.g. Time management, communication" data-testid="feedback-improvement" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFeedback(null)}>Cancel</Button>
            <Button onClick={handleFeedback} disabled={!feedbackForm.comments} data-testid="submit-feedback-btn">Submit Feedback</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ==================== SETTINGS DIALOG ==================== */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Settings size={18} className="text-indigo-500" /> Probation Settings</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500">Configure probation duration and milestones per department/designation. If no specific rule matches, default 90-day probation applies.</p>
            <Button size="sm" onClick={() => setSettingForm({ department_id: '', designation_id: '', duration_days: 90, milestone_days_str: '30,60,90' })} data-testid="add-setting-btn">
              <Plus size={14} className="mr-1" /> Add Rule
            </Button>

            {settings.length === 0 ? (
              <p className="text-sm text-center text-slate-400 py-4">No custom rules. Default: 90 days with 30/60/90 milestones.</p>
            ) : (
              <div className="space-y-2">
                {settings.map(s => (
                  <div key={s.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border" data-testid={`setting-${s.id}`}>
                    <div>
                      <p className="text-sm font-medium">{s.department_name || 'All Departments'} / {s.designation_name || 'All Designations'}</p>
                      <p className="text-xs text-slate-500">{s.duration_days} days | Milestones: {(s.milestone_days || []).join(', ')} days</p>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setSettingForm({ ...s, milestone_days_str: (s.milestone_days || []).join(', ') })} data-testid={`edit-setting-${s.id}`}>
                        <Settings size={14} />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDeleteSetting(s.id)}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Setting Form */}
            {settingForm && (
              <Card className="border-indigo-200">
                <CardContent className="p-4 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Department</Label>
                      <Select value={settingForm.department_id || 'all'} onValueChange={v => setSettingForm({ ...settingForm, department_id: v === 'all' ? '' : v })}>
                        <SelectTrigger data-testid="setting-dept"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Departments</SelectItem>
                          {departments.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Designation</Label>
                      <Select value={settingForm.designation_id || 'all'} onValueChange={v => setSettingForm({ ...settingForm, designation_id: v === 'all' ? '' : v })}>
                        <SelectTrigger data-testid="setting-desig"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Designations</SelectItem>
                          {designations.map(d => <SelectItem key={d.id} value={d.id}>{d.title || d.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label>Duration (days)</Label>
                    <Input type="number" value={settingForm.duration_days} onChange={e => setSettingForm({ ...settingForm, duration_days: e.target.value })} data-testid="setting-duration" />
                  </div>
                  <div>
                    <Label>Milestone Days (comma-separated)</Label>
                    <Input value={settingForm.milestone_days_str} onChange={e => setSettingForm({ ...settingForm, milestone_days_str: e.target.value })} placeholder="30, 60, 90" data-testid="setting-milestones" />
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleSaveSetting} data-testid="save-setting-btn">Save Rule</Button>
                    <Button size="sm" variant="outline" onClick={() => setSettingForm(null)}>Cancel</Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default ProbationTracker;
